/**
 * Pure utility functions for the retro canvas.
 *
 * All functions here are dependency-free and fully unit-testable.
 * CanvasSurface imports them so that the tested logic stays in sync with
 * what runs in production.
 */

// ─── Constants ────────────────────────────────────────────────────────────────

export const CANVAS_SIZE    = 20000; // board size in px; the dot grid fills this box
export const FIT_PADDING_PX = 120;  // screen-space padding when zooming to fit stickies
export const MAX_FIT_SCALE  = 1.5;  // don't zoom in beyond this when fitting
export const ZOOM_BASE      = 0.993; // exponential base per pixel of normalised delta
export const ZOOM_DELTA_CAP = 30;   // max normalised delta per wheel event

/** Screen-space pixel distance below which a pointer-down + pointer-up is treated as a click, not a drag. */
export const POINTER_DRAG_THRESHOLD_PX = 4;

/**
 * Max characters for sticky note body text.
 * Unbounded contenteditable input can freeze the tab (font-fit + layout on every keystroke).
 */
export const MAX_STICKY_CONTENT_LENGTH = 250;

/**
 * Max characters for title / text-label content.
 * Labels are single-line (`whiteSpace: nowrap`); unbounded text can crash layout.
 */
export const MAX_LABEL_CONTENT_LENGTH = 50;

/** Max stickies on one board. Keep in sync with RetroCanvasLimits.MaxStickies. */
export const MAX_STICKIES = 500;

/** Max text labels on one board. Keep in sync with RetroCanvasLimits.MaxTextLabels. */
export const MAX_LABELS = 300;

/**
 * Truncate `text` to `maxLength` characters. No-op when already within the limit.
 */
export function clampTextLength(text: string, maxLength: number): string {
  if (maxLength < 0 || text.length <= maxLength) return text;
  return text.slice(0, maxLength);
}

/**
 * Effective length of contenteditable text for max-length checks.
 * An empty editor often reports a lone "\\n"; treat that as zero length.
 */
export function contentEditableLength(innerText: string): number {
  if (!innerText || innerText === '\n') return 0;
  return innerText.length;
}

/**
 * How many characters of an incoming insert still fit under `maxLength`,
 * accounting for selected text that would be replaced.
 */
export function remainingContentCapacity(
  currentLength: number,
  selectedLength: number,
  maxLength: number,
): number {
  const replaced = Math.min(Math.max(selectedLength, 0), Math.max(currentLength, 0));
  return Math.max(0, maxLength - (currentLength - replaced));
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CanvasTransform {
  positionX: number;
  positionY: number;
  scale:     number;
}

export interface FitRect {
  x:      number;
  y:      number;
  width:  number;
  height: number;
}

export interface StoredViewport {
  x:     number;
  y:     number;
  scale: number;
}

// ─── Coordinate conversion ───────────────────────────────────────────────────

/**
 * Convert a screen-space point to board (canvas content) coordinates.
 *
 * screenX/Y are relative to the top-left of the canvas container element.
 */
export function screenToBoard(
  screenX: number,
  screenY: number,
  transform: CanvasTransform,
): { x: number; y: number } {
  return {
    x: (screenX - transform.positionX) / transform.scale,
    y: (screenY - transform.positionY) / transform.scale,
  };
}

/**
 * Convert a board-space point to screen (container-relative) coordinates.
 */
export function boardToScreen(
  boardX: number,
  boardY: number,
  transform: CanvasTransform,
): { x: number; y: number } {
  return {
    x: boardX * transform.scale + transform.positionX,
    y: boardY * transform.scale + transform.positionY,
  };
}

// ─── Viewport fitting ─────────────────────────────────────────────────────────

/**
 * Compute the transform that places a given board point at the viewport centre.
 *
 * `boardCentreX/Y` default to 0 (the board origin). Pass `CANVAS_SIZE / 2`
 * for new empty canvases so there is equal scroll room in every direction.
 */
export function computeCentreTransform(
  viewportW:    number,
  viewportH:    number,
  boardCentreX = 0,
  boardCentreY = 0,
): CanvasTransform {
  return {
    positionX: viewportW  / 2 - boardCentreX,
    positionY: viewportH  / 2 - boardCentreY,
    scale: 1,
  };
}

/**
 * Compute the transform that fits all supplied rects into the viewport with
 * `paddingPx` of screen-space padding, capped at `maxScale`.
 *
 * Returns null when `rects` is empty (caller should use computeCentreTransform).
 */
export function computeFitTransform(
  rects: FitRect[],
  viewportW: number,
  viewportH: number,
  paddingPx: number = FIT_PADDING_PX,
  maxScale:  number = MAX_FIT_SCALE,
): CanvasTransform | null {
  if (rects.length === 0) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }

  const boardW = maxX - minX;
  const boardH = maxY - minY;

  const scale = Math.min(
    (viewportW - paddingPx * 2) / boardW,
    (viewportH - paddingPx * 2) / boardH,
    maxScale,
  );

  const boardCentreX = minX + boardW / 2;
  const boardCentreY = minY + boardH / 2;

  return {
    positionX: viewportW / 2 - boardCentreX * scale,
    positionY: viewportH / 2 - boardCentreY * scale,
    scale,
  };
}

// ─── Pan bounds ───────────────────────────────────────────────────────────────

function clampPanAxis(
  position: number,
  scale: number,
  viewport: number,
  canvasSize: number,
): number {
  // A 0×0 viewport means layout has not run yet; clamping would treat the
  // board as larger than the view and pull the camera to the origin.
  if (viewport <= 0 || scale <= 0 || canvasSize <= 0) return position;
  const content = canvasSize * scale;
  if (content <= viewport) return (viewport - content) / 2;
  return Math.min(0, Math.max(viewport - content, position));
}

/**
 * Clamp a pan offset so the viewport cannot leave the board.
 *
 * When the scaled board is larger than the viewport, pan stops at the grid
 * edges. When the whole board is visible (zoomed out), it stays centred so
 * empty space around the grid cannot be panned into.
 */
export function clampPan(
  positionX: number,
  positionY: number,
  scale: number,
  viewportW: number,
  viewportH: number,
  canvasSize: number = CANVAS_SIZE,
): CanvasTransform {
  return {
    positionX: clampPanAxis(positionX, scale, viewportW, canvasSize),
    positionY: clampPanAxis(positionY, scale, viewportH, canvasSize),
    scale,
  };
}

// ─── Zoom factor ──────────────────────────────────────────────────────────────

/**
 * Convert a raw WheelEvent delta into a multiplicative zoom factor.
 *
 * - Normalises deltaMode (0 = px, 1 = lines × 16, 2 = pages × 600)
 * - Caps the normalised delta to `deltaCap` so a single large mouse-wheel
 *   tick can't jump the viewport
 * - Returns factor > 1 for zoom-in (negative delta) and < 1 for zoom-out
 */
export function computeZoomFactor(
  rawDelta: number,
  deltaMode: number,
  base:     number = ZOOM_BASE,
  deltaCap: number = ZOOM_DELTA_CAP,
): number {
  let delta = rawDelta;
  if (deltaMode === 1) delta *= 16;
  if (deltaMode === 2) delta *= 600;

  const capped = Math.sign(delta) * Math.min(Math.abs(delta), deltaCap);
  return Math.pow(base, capped);
}

// ─── Viewport persistence (sessionStorage) ───────────────────────────────────

export const viewportStorageKey = (roomUrlId: string) =>
  `retro-viewport-${roomUrlId}`;

export function loadViewport(roomUrlId: string): StoredViewport | null {
  try {
    const raw = sessionStorage.getItem(viewportStorageKey(roomUrlId));
    return raw ? (JSON.parse(raw) as StoredViewport) : null;
  } catch {
    return null;
  }
}

export function saveViewport(roomUrlId: string, vp: StoredViewport): void {
  try {
    sessionStorage.setItem(viewportStorageKey(roomUrlId), JSON.stringify(vp));
  } catch { /* storage full or unavailable — silently skip */ }
}

// ─── Canvas item helpers ──────────────────────────────────────────────────────

/**
 * Upsert an item into an immutable array by `id`.
 * Returns a new array with the item appended if not found, or with the item
 * merged (spread) at its existing index.
 */
export function mergeById<T extends { id: string }>(prev: T[], diff: T): T[] {
  const idx = prev.findIndex(item => item.id === diff.id);
  if (idx === -1) return [...prev, diff];
  const next = [...prev];
  next[idx] = { ...next[idx], ...diff };
  return next;
}

/**
 * Upsert many items by `id` in one pass. Used for batched drag-end commits
 * so N remote items become one React state update.
 */
export function mergeAllById<T extends { id: string }>(prev: T[], diffs: T[]): T[] {
  if (!diffs?.length) return prev;
  const byId = new Map(diffs.map(d => [d.id, d]));
  const seen = new Set<string>();
  const next = prev.map(item => {
    const diff = byId.get(item.id);
    if (!diff) return item;
    seen.add(item.id);
    return { ...item, ...diff };
  });
  diffs.forEach(d => {
    if (!seen.has(d.id)) next.push(d);
  });
  return next;
}

/**
 * Apply live-drag positions for one item kind. Unknown ids are ignored
 * (a move is not a create). Returns `prev` when nothing changed.
 */
export function applyItemMoves<T extends { id: string; x: number; y: number }>(
  prev: T[],
  moves: ReadonlyArray<{ id: string; kind: string; x: number; y: number }>,
  kind: 'sticky' | 'label',
): T[] {
  if (!moves?.length) return prev;
  const byId = new Map<string, { x: number; y: number }>();
  for (const move of moves) {
    if (move.kind === kind) byId.set(move.id, move);
  }
  if (byId.size === 0) return prev;
  let changed = false;
  const next = prev.map(item => {
    const move = byId.get(item.id);
    if (!move) return item;
    if (item.x === move.x && item.y === move.y) return item;
    changed = true;
    return { ...item, x: move.x, y: move.y };
  });
  return changed ? next : prev;
}

/** Fields that must round-trip on undo even when `updatedAt` was not bumped (live drag). */
interface CanvasRestoreFields {
  id: string;
  updatedAt: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  content?: string;
  colorSlot?: number;
  zIndex?: number;
  shape?: string;
  fontSize?: number;
}

function canvasRestoreItemChanged<T extends CanvasRestoreFields>(prev: T, restored: T): boolean {
  return prev.updatedAt !== restored.updatedAt
    || prev.x !== restored.x
    || prev.y !== restored.y
    || prev.width !== restored.width
    || prev.height !== restored.height
    || prev.content !== restored.content
    || prev.colorSlot !== restored.colorSlot
    || (prev.zIndex ?? 0) !== (restored.zIndex ?? 0)
    || prev.shape !== restored.shape
    || prev.fontSize !== restored.fontSize;
}

/**
 * Minimal undo/redo broadcast: upsert items that are new or whose persistable
 * fields changed (updatedAt, position, size, z, content, color, shape, fontSize);
 * delete items absent from the restored state. Unchanged items are skipped.
 */
export function diffCanvasRestore<
  TSticky extends CanvasRestoreFields,
  TLabel extends CanvasRestoreFields,
>(
  restoredStickies: TSticky[],
  restoredLabels: TLabel[],
  prevStickies: TSticky[],
  prevLabels: TLabel[],
): {
  upsertStickies: TSticky[];
  upsertLabels: TLabel[];
  deleteStickyIds: string[];
  deleteLabelIds: string[];
} {
  const upsertStickies: TSticky[] = [];
  const upsertLabels: TLabel[] = [];
  const deleteStickyIds: string[] = [];
  const deleteLabelIds: string[] = [];

  const prevStickyMap = new Map(prevStickies.map(s => [s.id, s]));
  restoredStickies.forEach(s => {
    const prev = prevStickyMap.get(s.id);
    if (!prev || canvasRestoreItemChanged(prev, s)) upsertStickies.push(s);
  });
  const restoredStickyIds = new Set(restoredStickies.map(s => s.id));
  prevStickies.forEach(s => { if (!restoredStickyIds.has(s.id)) deleteStickyIds.push(s.id); });

  const prevLabelMap = new Map(prevLabels.map(l => [l.id, l]));
  restoredLabels.forEach(l => {
    const prev = prevLabelMap.get(l.id);
    if (!prev || canvasRestoreItemChanged(prev, l)) upsertLabels.push(l);
  });
  const restoredLabelIds = new Set(restoredLabels.map(l => l.id));
  prevLabels.forEach(l => { if (!restoredLabelIds.has(l.id)) deleteLabelIds.push(l.id); });

  return { upsertStickies, upsertLabels, deleteStickyIds, deleteLabelIds };
}

export function wouldExceedCanvasCaps(stickyCount: number, labelCount: number): boolean {
  return stickyCount > MAX_STICKIES || labelCount > MAX_LABELS;
}

/**
 * Returns the rendered width and height of a sticky note, accounting for
 * square vs rectangle shapes. Square stickies use `min(width, height)` as
 * both dimensions so they stay visually square regardless of the stored values.
 */
export function stickyDisplaySize(
  width: number,
  height: number,
  shape?: string,
): { w: number; h: number } {
  const isSquare = shape !== 'rectangle';
  const side     = Math.min(width, height);
  return {
    w: isSquare ? side : width,
    h: isSquare ? side : height,
  };
}

// ─── Z-order (shared by stickies and text labels) ─────────────────────────────

/** Temporary CSS boost while an item is being dragged so it stays above peers. */
export const MOVING_Z_BOOST = 1_000_000;

/** Effective stacking value for an item (missing zIndex treated as 0). */
export function itemZIndex(item: { zIndex?: number }): number {
  return item.zIndex ?? 0;
}

/**
 * Highest zIndex across stickies and labels, or 0 when the board is empty.
 * Stickies and labels share one stack so either type can sit above the other.
 */
export function maxZIndex(
  stickies: { zIndex?: number }[],
  labels: { zIndex?: number }[],
): number {
  let max = 0;
  for (const s of stickies) max = Math.max(max, itemZIndex(s));
  for (const l of labels) max = Math.max(max, itemZIndex(l));
  return max;
}

/** Next free zIndex above every sticky and label on the board. */
export function nextZIndex(
  stickies: { zIndex?: number }[],
  labels: { zIndex?: number }[],
): number {
  return maxZIndex(stickies, labels) + 1;
}

/**
 * CSS z-index for a canvas item. While moving, boosts above all resting items
 * without changing the persisted zIndex.
 */
export function displayZIndex(item: { zIndex?: number }, isMoving: boolean): number {
  const base = itemZIndex(item);
  return isMoving ? base + MOVING_Z_BOOST : base;
}

export interface BringToFrontOrder {
  /** Selected sticky ids, ordered by their current zIndex ascending. */
  orderedStickyIds: string[];
  /** Selected label ids, ordered by their current zIndex ascending. */
  orderedLabelIds: string[];
  /** First zIndex to assign — orderedStickyIds get baseZ, baseZ+1, …, then orderedLabelIds continue the sequence. */
  baseZ: number;
}

/**
 * Compute the order (and starting zIndex) to raise the given sticky/label ids to the
 * top of the shared stack. Ids within each kind keep their relative order; stickies as
 * a group are placed below labels as a group (both compact protocol messages assign
 * stickies first, then labels — see RETRO_SYNC_STRATEGY.md), a small trade-off for a
 * payload that only needs two flat id lists instead of a per-item zIndex map. Returns
 * null when the selection already occupies the top of the stack (no write needed).
 */
export function computeBringToFrontOrder(
  stickies: { id: string; zIndex?: number }[],
  labels: { id: string; zIndex?: number }[],
  stickyIds: string[],
  labelIds: string[],
): BringToFrontOrder | null {
  if (stickyIds.length === 0 && labelIds.length === 0) return null;

  const stickyIdSet = new Set(stickyIds);
  const labelIdSet = new Set(labelIds);
  const stickyTargets = stickies.filter(s => stickyIdSet.has(s.id));
  const labelTargets = labels.filter(l => labelIdSet.has(l.id));
  if (stickyTargets.length === 0 && labelTargets.length === 0) return null;

  // Already on top when every non-selected item sits strictly below the lowest
  // selected item — comparing z-values here (rather than which items hold them)
  // would wrongly treat a tie (e.g. every item still at the default zIndex 0)
  // as "already on top" and skip the promotion entirely.
  let targetsMinZ = Infinity;
  for (const s of stickyTargets) targetsMinZ = Math.min(targetsMinZ, itemZIndex(s));
  for (const l of labelTargets) targetsMinZ = Math.min(targetsMinZ, itemZIndex(l));

  let othersMaxZ = -Infinity;
  for (const s of stickies) {
    if (!stickyIdSet.has(s.id)) othersMaxZ = Math.max(othersMaxZ, itemZIndex(s));
  }
  for (const l of labels) {
    if (!labelIdSet.has(l.id)) othersMaxZ = Math.max(othersMaxZ, itemZIndex(l));
  }
  if (othersMaxZ === -Infinity || targetsMinZ > othersMaxZ) {
    return null;
  }

  return {
    orderedStickyIds: [...stickyTargets].sort((a, b) => itemZIndex(a) - itemZIndex(b)).map(s => s.id),
    orderedLabelIds: [...labelTargets].sort((a, b) => itemZIndex(a) - itemZIndex(b)).map(l => l.id),
    baseZ: nextZIndex(stickies, labels),
  };
}

/**
 * Apply a bring-to-front z sequence to one item array: assigns zIndex = baseZ,
 * baseZ+1, … to items whose id appears in `orderedIds`, in that order, and bumps
 * their `updatedAt`. Items not listed are returned unchanged. Shared by the sender
 * (optimistic local update) and receivers (applying an incoming itemsBroughtToFront
 * broadcast) so both compute identical results.
 */
export function applyZIndexSequence<T extends { id: string; zIndex?: number; updatedAt?: string }>(
  items: T[],
  orderedIds: string[],
  baseZ: number,
  updatedAt: string,
): T[] {
  if (orderedIds.length === 0) return items;
  const zById = new Map(orderedIds.map((id, i) => [id, baseZ + i]));
  let changed = false;
  const next = items.map(item => {
    const z = zById.get(item.id);
    if (z === undefined) return item;
    changed = true;
    return { ...item, zIndex: z, updatedAt };
  });
  return changed ? next : items;
}

// ─── Selection rectangle ──────────────────────────────────────────────────────

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Returns true when two axis-aligned bounding boxes overlap.
 * Touching edges and zero-dimension rects are NOT considered intersections,
 * which prevents a collapsed lasso rect from accidentally selecting stickies.
 */
export function rectsIntersect(r1: Rect, r2: Rect): boolean {
  return r1.w > 0 && r1.h > 0 && r2.w > 0 && r2.h > 0 &&
         r1.x < r2.x + r2.w &&
         r1.x + r1.w > r2.x &&
         r1.y < r2.y + r2.h &&
         r1.y + r1.h > r2.y;
}
